import { Constructable, Container, Service } from "typedi";
import axios, { AxiosRequestConfig } from "axios";
import { API_URL } from "@/core/environment";
import { HttpInterceptor } from "~shared/classes/http-interceptor";

@Service()
export class HttpService {
  private readonly _http = axios.create({
    baseURL: `${API_URL}/api`,
    withCredentials: true,
  });

  private bindInterceptor(interceptor: HttpInterceptor): void {
    this._http.interceptors.request.use(
      interceptor.onRequest.bind(interceptor),
      interceptor.onRequestError.bind(interceptor)
    );
    this._http.interceptors.response.use(
      interceptor.onResponse.bind(interceptor),
      interceptor.onResponseError.bind(interceptor)
    );
  }

  public useInterceptors(...interceptors: Constructable<HttpInterceptor>[]): void {
    interceptors.forEach((interceptor) => this.bindInterceptor(Container.get(interceptor)));
  }

  public get<T>(url: string, params?: Record<string, unknown>, options: Partial<AxiosRequestConfig> = {}): Promise<T> {
    return this._http.get<T>(url, { ...options, params }).then((response) => response.data);
  }

  public post<T, B>(url: string, body?: B, options: Partial<AxiosRequestConfig> = {}): Promise<T> {
    return this._http.post<T>(url, body, options).then((response) => response.data);
  }

  public put<T, B>(url: string, body?: B, options: Partial<AxiosRequestConfig> = {}): Promise<T> {
    return this._http.put(url, body, options).then((response) => response.data);
  }

  public patch<T, B>(url: string, body?: B, options: Partial<AxiosRequestConfig> = {}): Promise<T> {
    return this._http.patch(url, body, options).then((response) => response.data);
  }

  public delete<T>(
    url: string,
    params?: Record<string, unknown>,
    options: Partial<AxiosRequestConfig> = {}
  ): Promise<T> {
    return this._http
      .delete(url, {
        ...options,
        params,
      })
      .then((response) => response.data);
  }
}
